import { terms } from "./docs/terms";
import { privacy } from "./docs/privacy";
import { platformRules } from "./docs/platform-rules";
import { eula } from "./docs/eula";
import { cookie } from "./docs/cookie";
import { paymentTerms } from "./docs/payment-terms";
import { communityGuidelines } from "./docs/community-guidelines";
import { dataDeletion } from "./docs/data-deletion";
import { employerAgreement } from "./docs/employer-agreement";
import { workerConduct } from "./docs/worker-conduct";
import { cauHoiThuongGap } from "./docs/cau-hoi-thuong-gap";
import { veChungToi } from "./docs/ve-chung-toi";
import type { LegalDoc, LegalManifest, LegalManifestEntry } from "./types";

export { LEGAL_COMPANY } from "./company";
export type {
  LegalDoc,
  LegalSection,
  LegalContact,
  LegalManifest,
  LegalManifestEntry,
} from "./types";

// Source order = navigation order in footer / settings menu.
export const LEGAL_DOCS: readonly LegalDoc[] = [
  terms,
  privacy,
  platformRules,
  paymentTerms,
  workerConduct,
  employerAgreement,
  communityGuidelines,
  dataDeletion,
  cookie,
  eula,
  cauHoiThuongGap,
  veChungToi,
] as const;

export type LegalSlug = (typeof LEGAL_DOCS)[number]["slug"];

const BY_SLUG: Record<string, LegalDoc> = Object.fromEntries(
  LEGAL_DOCS.map((d) => [d.slug, d]),
);

export function getLegalDoc(slug: string): LegalDoc | null {
  return BY_SLUG[slug] ?? null;
}

export function getLegalManifest(): LegalManifest {
  const docs: LegalManifestEntry[] = LEGAL_DOCS.map((d) => ({
    slug: d.slug,
    title: d.title,
    version: d.version,
    effectiveDate: d.effectiveDate,
  }));
  return { generatedAt: new Date().toISOString(), docs };
}

// ─────────────────────────────────────────────────────────────
// Điều hướng — dùng cho sidebar web và footer.
// Nhóm theo chủ đề người đọc quan tâm, không theo thứ tự pháp lý.
// ─────────────────────────────────────────────────────────────

export type LegalGroupDef = { label: string; slugs: readonly string[] };
export type LegalGroup = { label: string; docs: readonly LegalDoc[] };

export const LEGAL_GROUPS: readonly LegalGroupDef[] = [
  { label: "Điều khoản sử dụng", slugs: ["terms", "eula"] },
  {
    label: "Quyền riêng tư & dữ liệu",
    slugs: ["privacy", "cookie", "data-deletion"],
  },
  { label: "Thanh toán", slugs: ["payment-terms"] },
  {
    label: "Quy tắc & vận hành",
    slugs: [
      "platform-rules",
      "community-guidelines",
      "worker-conduct",
      "employer-agreement",
    ],
  },
  { label: "Hỗ trợ", slugs: ["cau-hoi-thuong-gap", "ve-chung-toi"] },
] as const;

// Doc mới thêm mà quên khai báo nhóm sẽ rơi vào "Khác" thay vì biến mất khỏi nav.
const UNGROUPED_LABEL = "Khác";

export function getLegalGroups(): LegalGroup[] {
  const claimed = new Set<string>();
  const groups: LegalGroup[] = [];

  for (const g of LEGAL_GROUPS) {
    const docs = g.slugs
      .map((s) => {
        claimed.add(s);
        return BY_SLUG[s];
      })
      .filter((d): d is LegalDoc => Boolean(d));
    if (docs.length > 0) groups.push({ label: g.label, docs });
  }

  const rest = LEGAL_DOCS.filter((d) => !claimed.has(d.slug));
  if (rest.length > 0) groups.push({ label: UNGROUPED_LABEL, docs: rest });

  return groups;
}

export function getLegalGroupLabel(slug: string): string | null {
  for (const g of LEGAL_GROUPS) {
    if (g.slugs.includes(slug)) return g.label;
  }
  return BY_SLUG[slug] ? UNGROUPED_LABEL : null;
}

/** Doc liền trước / liền sau theo thứ tự sidebar, để render pager cuối trang. */
export function getLegalNeighbors(slug: string): {
  prev: LegalDoc | null;
  next: LegalDoc | null;
} {
  const flat = getLegalGroups().flatMap((g) => g.docs);
  const i = flat.findIndex((d) => d.slug === slug);
  if (i === -1) return { prev: null, next: null };
  return { prev: flat[i - 1] ?? null, next: flat[i + 1] ?? null };
}

/**
 * Chữ được tô nhấn (gạch chân lime) trong tiêu đề trang.
 * Là lựa chọn biên tập nên khai báo tay — không đoán bằng heuristic vì
 * độ dài tiêu đề chênh nhau quá nhiều.
 */
export const LEGAL_TITLE_ACCENT: Readonly<Record<string, string>> = {
  terms: "sử dụng",
  privacy: "bảo mật",
  "platform-rules": "nền tảng",
  "payment-terms": "thanh toán",
  "worker-conduct": "Người tìm việc",
  "employer-agreement": "Nhà tuyển dụng",
  "community-guidelines": "cộng đồng",
  "data-deletion": "xoá dữ liệu",
  cookie: "Cookie",
  eula: "người dùng cuối",
  "cau-hoi-thuong-gap": "thường gặp",
  "ve-chung-toi": "Chốt Ca",
};

// ─────────────────────────────────────────────────────────────
// Thời gian đọc ước tính
// ─────────────────────────────────────────────────────────────

// Văn bản pháp lý đọc chậm hơn nội dung thường; 180 từ/phút là mức thực tế.
const WORDS_PER_MINUTE = 180;

function countWords(text: string | undefined): number {
  if (!text) return 0;
  // Bỏ cú pháp inline-markdown để **đậm** và [nhãn](url) không thổi phồng số từ.
  const plain = text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*/g, "");
  const words = plain.trim().split(/\s+/);
  return words[0] === "" ? 0 : words.length;
}

function countSection(s: LegalSectionLike): number {
  return (
    countWords(s.title) +
    countWords(s.body) +
    (s.items?.reduce((n, it) => n + countWords(it), 0) ?? 0) +
    (s.sections?.reduce((n, sub) => n + countSection(sub), 0) ?? 0)
  );
}

type LegalSectionLike = {
  title: string;
  body?: string;
  items?: string[];
  sections?: LegalSectionLike[];
};

export function estimateReadingMinutes(doc: LegalDoc): number {
  const total =
    countWords(doc.intro) +
    doc.sections.reduce((n, s) => n + countSection(s), 0) +
    countWords(doc.contact?.body);
  return Math.max(1, Math.round(total / WORDS_PER_MINUTE));
}
